import { NavLink, useNavigate } from 'react-router-dom';
import { useEquipo } from '../../context/EquipoContext';
import { useAuth }   from '../../context/AuthContext';
import { useEffect, useState } from 'react';
import { getDesafiosEquipo } from '../../services/api';

const ADMIN_EMAIL = 'pedro.salfatte@techbrain.cl';

export default function Sidebar() {
  const { equipo, deporteActivo } = useEquipo();
  const { token, user }           = useAuth();
  const navigate                  = useNavigate();
  const [badgeDesafios, setBadgeDesafios] = useState(0);
  const [badgePartidos, setBadgePartidos] = useState(0);

  const esAdmin  = user?.email === ADMIN_EMAIL;
  const esFutbol = !deporteActivo || deporteActivo === 'futbol';

  useEffect(() => {
    if (!equipo?.id || !token) return;
    const cargar = () => {
      getDesafiosEquipo(equipo.id, token)
        .then(data => {
          if (!Array.isArray(data)) return;
          const recibidosPendientes = data.filter(d =>
            d.equipo_visitante_id === equipo.id && d.estado === 'pendiente'
          ).length;
          const enviadosAceptados = data.filter(d =>
            d.equipo_local_id === equipo.id &&
            (d.estado === 'aceptado' || d.estado === 'confirmado')
          ).length;
          const enviadosRechazados = data.filter(d =>
            d.equipo_local_id === equipo.id && d.estado === 'rechazado'
          ).length;
          setBadgeDesafios(recibidosPendientes + enviadosAceptados + enviadosRechazados);
          const confirmados = data.filter(d =>
            (d.equipo_local_id === equipo.id || d.equipo_visitante_id === equipo.id) &&
            (d.estado === 'aceptado' || d.estado === 'confirmado')
          ).length;
          setBadgePartidos(confirmados);
        })
        .catch(() => {});
    };
    cargar();
    const intervalo = setInterval(cargar, 30000);
    return () => clearInterval(intervalo);
  }, [equipo, token]);

  // Sección "Mi equipo" cambia según deporte
  const seccionMiEquipo = esFutbol
    ? { label: 'Mi equipo', items: [
        { label: 'Nómina',       path: '/app/nomina',       icono: '◫' },
        { label: 'Estadísticas', path: '/app/estadisticas', icono: '★' },
      ]}
    : { label: `Mi perfil — ${deporteActivo === 'padel' ? 'Pádel' : 'Tenis'}`, items: [
        { label: 'Mis Parejas',  path: '/app/nomina',       icono: '🤝' },
        { label: 'Estadísticas', path: '/app/estadisticas', icono: '★' },
      ]};

  const NAV_ITEMS = [
    {
      section: 'Principal',
      items: [
        { label: 'Dashboard',    path: '/app/dashboard',   icono: '⊞' },
        { label: 'Buscar rival', path: '/app/matchmaking', icono: '◎' },
        { label: 'Desafíos',     path: '/app/desafios',    icono: '⚡', badge: badgeDesafios },
        { label: 'Mis partidos', path: '/app/partidos',    icono: '▦',  badge: badgePartidos },
        { label: 'Ranking',      path: '/app/ranking',     icono: '▲' },
      ]
    },
    // Servicios solo para fútbol
    ...(esFutbol ? [{
      section: 'Servicios',
      items: [
        { label: 'Recintos',      path: '/app/recintos',      icono: '◉' },
        { label: 'Árbitros',      path: '/app/arbitros',      icono: '◈' },
        { label: 'Tercer Tiempo', path: '/app/tercer-tiempo', icono: '☕' },
        { label: 'Panel Recinto', path: '/app/panel-recinto', icono: '🏟️' },
      ]
    }] : []),
    {
      section: seccionMiEquipo.label,
      items: seccionMiEquipo.items,
    }
  ];

  // ELO según deporte
  const campoElo = esFutbol ? 'elo' : deporteActivo === 'padel' ? 'elo_padel' : 'elo_tenis';
  const eloActual = equipo?.[campoElo] || equipo?.elo || 1000;

  return (
    <aside style={styles.sidebar}>
      {NAV_ITEMS.map(({ section, items }) => (
        <div key={section}>
          <div style={styles.sectionLabel}>{section}</div>
          {items.map(({ label, path, icono, badge }) => (
            <NavLink
              key={path}
              to={path}
              style={({ isActive }) => ({
                ...styles.navItem,
                background: isActive ? 'rgba(26,107,255,0.18)' : 'transparent',
              })}
            >
              {({ isActive }) => (
                <>
                  <span style={{ ...styles.icono, color: isActive ? 'white' : 'rgba(255,255,255,0.35)' }}>
                    {icono}
                  </span>
                  <span style={{ ...styles.navLabel, color: isActive ? 'white' : 'rgba(255,255,255,0.4)' }}>
                    {label}
                  </span>
                  {badge > 0 && (
                    <span style={styles.badge}>{badge}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}

      {esAdmin && (
        <div style={{ marginTop: 12 }}>
          <div style={styles.sectionLabel}>Administración</div>
          <div style={styles.adminBtn} onClick={() => navigate('/admin')}>
            <span style={styles.adminIcon}>⚙</span>
            <span style={styles.adminLabel}>Panel Admin</span>
            <span style={styles.adminPill}>Admin</span>
          </div>
        </div>
      )}

      {equipo && (
        <div style={styles.eloWidget}>
          <div style={styles.eloLabel}>Mi ELO — {esFutbol ? 'Fútbol' : deporteActivo === 'padel' ? 'Pádel' : 'Tenis'}</div>
          <div style={styles.eloNumber}>{eloActual}</div>
          <div style={styles.eloSub}>{equipo.nombre}</div>
          {equipo.zona && (
            <div style={styles.rankBadge}>{equipo.zona}</div>
          )}
        </div>
      )}
    </aside>
  );
}

const styles = {
  sidebar: {
    width: 220, minWidth: 220,
    background: '#161C26',
    borderRight: '1px solid rgba(255,255,255,0.05)',
    padding: '16px 10px',
    display: 'flex', flexDirection: 'column',
    gap: 2, overflowY: 'auto', height: '100%',
  },
  sectionLabel: {
    fontSize: 10, fontWeight: 700,
    color: 'rgba(255,255,255,0.2)',
    textTransform: 'uppercase', letterSpacing: '0.08em',
    padding: '14px 10px 4px',
    fontFamily: "'Barlow', sans-serif",
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', borderRadius: 9,
    cursor: 'pointer', textDecoration: 'none',
    transition: 'background 0.12s', width: '100%',
  },
  icono: { fontSize: 14, width: 16, textAlign: 'center', flexShrink: 0 },
  navLabel: {
    fontSize: 13, fontWeight: 500,
    fontFamily: "'Barlow', sans-serif", flex: 1,
  },
  badge: {
    background: '#FF6B1A', color: 'white',
    fontSize: 9, fontWeight: 700,
    padding: '1px 6px', borderRadius: 8,
    fontFamily: "'Barlow', sans-serif",
  },
  adminBtn: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', borderRadius: 9,
    cursor: 'pointer', transition: 'background 0.12s',
    background: 'rgba(255,107,26,0.08)',
    border: '1px solid rgba(255,107,26,0.2)',
    marginBottom: 4,
  },
  adminIcon: { fontSize: 14, width: 16, textAlign: 'center', color: '#FF6B1A', flexShrink: 0 },
  adminLabel: { fontSize: 13, fontWeight: 500, flex: 1, color: '#FF6B1A', fontFamily: "'Barlow', sans-serif" },
  adminPill: {
    background: '#FF6B1A', color: 'white',
    fontSize: 9, fontWeight: 700,
    padding: '2px 7px', borderRadius: 8,
    fontFamily: "'Barlow', sans-serif",
    letterSpacing: '0.04em',
  },
  eloWidget: {
    marginTop: 'auto',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12, padding: 14,
  },
  eloLabel: {
    fontSize: 9, fontWeight: 700,
    color: 'rgba(255,255,255,0.25)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 5, fontFamily: "'Barlow', sans-serif",
  },
  eloNumber: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 30, fontWeight: 800,
    color: 'white', lineHeight: 1, marginBottom: 2,
  },
  eloSub: {
    fontSize: 11, color: 'rgba(255,255,255,0.3)',
    fontFamily: "'Barlow', sans-serif", marginBottom: 6,
  },
  rankBadge: {
    display: 'inline-block',
    background: '#FF6B1A', color: 'white',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 11, fontWeight: 700,
    padding: '3px 10px', borderRadius: 20,
  },
};
